import {
  aiImportRecipeResponseSchema,
  aiJobStatusResponseSchema,
  cleanRecipeOutputSchema,
  type AiImportRecipeResponse,
  type AiJobStatusResponse,
  type CleanRecipeOutput,
} from '@awwyeah/shared';
import type { RecipeDraft } from './hooks';

/**
 * The recipes module's client for `/api/ai/import-recipe` — the "✨ clean it up" path on
 * the form's import panel.
 *
 * This lives in `recipes/`, not in the `ai` module, on purpose: it is recipe-shaped
 * knowledge (how a `clean-recipe` result becomes a `RecipeDraft`), and modules don't reach
 * into each other's internals. The `ai` module publishes the generic quick-task hook; this
 * file publishes the recipe-specific one.
 */

export class AiImportError extends Error {}

export async function requestAiImport(url: string): Promise<AiImportRecipeResponse> {
  let response: Response;
  try {
    response = await fetch('/api/ai/import-recipe', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ url }),
    });
  } catch {
    throw new AiImportError('network_error');
  }

  const parsed = aiImportRecipeResponseSchema.safeParse(await response.json().catch(() => null));
  if (!parsed.success) throw new AiImportError('unexpected_response');
  return parsed.data;
}

export async function fetchAiJob(jobId: string): Promise<AiJobStatusResponse> {
  const response = await fetch(`/api/ai/jobs/${encodeURIComponent(jobId)}`, {
    credentials: 'same-origin',
  });
  if (!response.ok) throw new AiImportError(`job_fetch_failed_${response.status}`);
  const parsed = aiJobStatusResponseSchema.safeParse(await response.json().catch(() => null));
  if (!parsed.success) throw new AiImportError('unexpected_response');
  return parsed.data;
}

export interface PollOptions {
  intervalMs?: number;
  timeoutMs?: number;
  signal?: AbortSignal;
  /** Injected in tests; defaults to `setTimeout`. */
  sleep?: (ms: number) => Promise<void>;
}

export type PollOutcome =
  | { status: 'done'; result: CleanRecipeOutput }
  /** The job is still waiting — usually because the assistant is napping. */
  | { status: 'waiting' }
  | { status: 'invalid' }
  | { status: 'failed' };

/**
 * Polls a `clean-recipe` job to completion.
 *
 * Bounded on purpose: a job whose relay is down can sit queued for a quarter of an hour of
 * backoff, and nobody stares at a form that long. Giving up returns `waiting`, not an
 * error — the job keeps draining server-side and the next visit sees the cleaned recipe.
 */
export async function pollAiJob(jobId: string, options: PollOptions = {}): Promise<PollOutcome> {
  const intervalMs = options.intervalMs ?? 1500;
  const timeoutMs = options.timeoutMs ?? 90_000;
  const sleep = options.sleep ?? ((ms: number) => new Promise((r) => setTimeout(r, ms)));

  const deadline = Date.now() + timeoutMs;
  for (;;) {
    if (options.signal?.aborted) return { status: 'waiting' };
    const job = await fetchAiJob(jobId);

    if (job.status === 'done') {
      const parsed = cleanRecipeOutputSchema.safeParse(job.result);
      // The relay already validated this; a mismatch here means the shared schema and the
      // stored result have drifted, which is a bug — but not one worth losing the form over.
      if (!parsed.success) return { status: 'invalid' };
      return { status: 'done', result: parsed.data };
    }
    if (job.status === 'invalid') return { status: 'invalid' };
    if (job.status === 'failed') return { status: 'failed' };

    if (Date.now() + intervalMs > deadline) return { status: 'waiting' };
    await sleep(intervalMs);
  }
}

/**
 * Turns a validated `clean-recipe` result into form state.
 *
 * `text` is always kept as the ingredient's displayed line — the spec's "a bad distill can
 * never lose information" rule, enforced here rather than trusted. The structured fields
 * ride alongside as the parsed metadata the scaling core reads.
 *
 * This never saves. The user reviews the filled-in form and presses save themselves.
 */
export function draftFromCleanRecipe(
  result: CleanRecipeOutput,
  previous: RecipeDraft,
): RecipeDraft {
  return {
    ...previous,
    title: result.title || previous.title,
    ingredients:
      result.ingredients.length > 0
        ? result.ingredients.map((ingredient) => ({
            text: ingredient.text,
            ...(ingredient.quantity !== null ? { quantity: String(ingredient.quantity) } : {}),
            ...(ingredient.quantityMax !== null
              ? { quantityMax: String(ingredient.quantityMax) }
              : {}),
            ...(ingredient.unit !== null ? { unit: ingredient.unit } : {}),
            ...(ingredient.name !== null ? { name: ingredient.name } : {}),
            ...(ingredient.prep !== null ? { prep: ingredient.prep } : {}),
            ...(ingredient.optional ? { optional: true } : {}),
          }))
        : previous.ingredients,
    // An empty `steps` is a legitimate, honest answer (the Cozi recipes whose directions
    // were never saved) — don't overwrite whatever the user already has with nothing.
    steps: result.steps.length > 0 ? result.steps : previous.steps,
    ...(result.servings !== null ? { servings: result.servings } : {}),
    ...(result.notes ? { notes: result.notes } : {}),
  };
}
